import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Filter,
  Calendar as CalendarIcon,
  X,
  RotateCcw,
  Download,
  Users,
  Building,
  DollarSign,
  Tag,
  Clock,
} from 'lucide-react';
import { format, addDays, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface AdvancedFiltersProps {
  filters: {
    startDate: string;
    endDate: string;
    clientId?: string;
    categoryId?: string;
    userId?: string;
    minValue?: number;
    maxValue?: number;
  };
  onFiltersChange: (filters: Partial<AdvancedFiltersProps['filters']>) => void;
  onClearFilters: () => void;
  loading?: boolean;
}

const presetPeriods = [
  {
    label: 'Últimos 7 dias',
    value: '7d',
    getDates: () => ({
      start: format(addDays(new Date(), -7), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'Últimos 30 dias',
    value: '30d',
    getDates: () => ({
      start: format(addDays(new Date(), -30), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'Este mês',
    value: 'this_month',
    getDates: () => ({
      start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      end: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'Mês passado',
    value: 'last_month',
    getDates: () => {
      const lastMonth = subMonths(new Date(), 1);
      return {
        start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
        end: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
      };
    },
  },
  {
    label: 'Últimos 3 meses',
    value: '3m',
    getDates: () => ({
      start: format(subMonths(new Date(), 3), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'Últimos 6 meses',
    value: '6m',
    getDates: () => ({
      start: format(subMonths(new Date(), 6), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'Este ano',
    value: 'this_year',
    getDates: () => ({
      start: format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    }),
  },
];

// Mock data for dropdowns - in real implementation, these would come from API
const mockClients = [
  { id: '1', name: 'Empresa ABC Ltda' },
  { id: '2', name: 'Tech Solutions S/A' },
  { id: '3', name: 'Digital Agency' },
  { id: '4', name: 'Startup X' },
];

const mockCategories = [
  { id: '1', name: 'Desenvolvimento Web' },
  { id: '2', name: 'Consultoria em TI' },
  { id: '3', name: 'Marketing Digital' },
  { id: '4', name: 'Design Gráfico' },
  { id: '5', name: 'Suporte Técnico' },
];

const mockUsers = [
  { id: '1', name: 'João Silva' },
  { id: '2', name: 'Maria Santos' },
  { id: '3', name: 'Pedro Costa' },
  { id: '4', name: 'Ana Oliveira' },
];

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <Badge variant="secondary" className="gap-1 pr-1">
      {label}
      <X className="h-3 w-3 cursor-pointer hover:text-destructive" onClick={onRemove} />
    </Badge>
  );
}

export function AdvancedFilters({
  filters,
  onFiltersChange,
  onClearFilters,
  loading = false,
}: AdvancedFiltersProps) {
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  const handlePresetPeriod = (preset: typeof presetPeriods[0]) => {
    const dates = preset.getDates();
    onFiltersChange({
      startDate: dates.start,
      endDate: dates.end,
    });
  };

  const hasActiveFilters = Boolean(
    filters.clientId ||
    filters.categoryId ||
    filters.userId ||
    filters.minValue ||
    filters.maxValue ||
    filters.startDate !== presetPeriods[5].getDates().start || // 6 months default
    filters.endDate !== presetPeriods[5].getDates().end
  );

  const activeFiltersList = [];

  if (filters.clientId) {
    const client = mockClients.find(c => c.id === filters.clientId);
    if (client) activeFiltersList.push({ label: `Cliente: ${client.name}`, key: 'clientId' });
  }

  if (filters.categoryId) {
    const category = mockCategories.find(c => c.id === filters.categoryId);
    if (category) activeFiltersList.push({ label: `Categoria: ${category.name}`, key: 'categoryId' });
  }

  if (filters.userId) {
    const user = mockUsers.find(u => u.id === filters.userId);
    if (user) activeFiltersList.push({ label: `Vendedor: ${user.name}`, key: 'userId' });
  }

  if (filters.minValue) {
    activeFiltersList.push({ label: `Min: R$ ${filters.minValue.toLocaleString('pt-BR')}`, key: 'minValue' });
  }

  if (filters.maxValue) {
    activeFiltersList.push({ label: `Max: R$ ${filters.maxValue.toLocaleString('pt-BR')}`, key: 'maxValue' });
  }

  const removeFilter = (key: string) => {
    onFiltersChange({ [key]: key === 'minValue' || key === 'maxValue' ? undefined : '' });
  };

  return (
    <div className="space-y-4">
      {/* Filter Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtros Avançados
            </CardTitle>
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={onClearFilters}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Limpar
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Active Filters */}
          {activeFiltersList.length > 0 && (
            <div className="mb-4">
              <div className="text-sm text-muted-foreground mb-2">Filtros ativos:</div>
              <div className="flex flex-wrap gap-2">
                {activeFiltersList.map((filter) => (
                  <FilterChip
                    key={filter.key}
                    label={filter.label}
                    onRemove={() => removeFilter(filter.key)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Period Presets */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Período</Label>
              <div className="grid grid-cols-2 gap-2">
                {presetPeriods.slice(0, 4).map((preset) => (
                  <Button
                    key={preset.value}
                    variant="outline"
                    size="sm"
                    onClick={() => handlePresetPeriod(preset)}
                    className={cn(
                      'text-xs h-8',
                      filters.startDate === preset.getDates().start &&
                      filters.endDate === preset.getDates().end &&
                      'bg-primary text-primary-foreground'
                    )}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {presetPeriods.slice(4).map((preset) => (
                  <Button
                    key={preset.value}
                    variant="outline"
                    size="sm"
                    onClick={() => handlePresetPeriod(preset)}
                    className={cn(
                      'text-xs h-8',
                      filters.startDate === preset.getDates().start &&
                      filters.endDate === preset.getDates().end &&
                      'bg-primary text-primary-foreground'
                    )}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Data Inicial</Label>
              <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !filters.startDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.startDate
                      ? format(new Date(filters.startDate), 'PPP', { locale: ptBR })
                      : 'Selecionar data'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.startDate ? new Date(filters.startDate) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        onFiltersChange({ startDate: format(date, 'yyyy-MM-dd') });
                      }
                      setStartDateOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Label className="text-sm font-medium mt-2">Data Final</Label>
              <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !filters.endDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.endDate
                      ? format(new Date(filters.endDate), 'PPP', { locale: ptBR })
                      : 'Selecionar data'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.endDate ? new Date(filters.endDate) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        onFiltersChange({ endDate: format(date, 'yyyy-MM-dd') });
                      }
                      setEndDateOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Client Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Building className="h-4 w-4" />
                Cliente
              </Label>
              <Select
                value={filters.clientId || 'none'}
                onValueChange={(value) => onFiltersChange({ clientId: value === 'none' ? undefined : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os clientes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Todos os clientes</SelectItem>
                  {mockClients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Categoria
              </Label>
              <Select
                value={filters.categoryId || 'none'}
                onValueChange={(value) => onFiltersChange({ categoryId: value === 'none' ? undefined : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas as categorias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Todas as categorias</SelectItem>
                  {mockCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator className="my-4" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* User Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Vendedor
              </Label>
              <Select
                value={filters.userId || 'none'}
                onValueChange={(value) => onFiltersChange({ userId: value === 'none' ? undefined : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os vendedores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Todos os vendedores</SelectItem>
                  {mockUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Value Range */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Faixa de Valor
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Mínimo"
                  value={filters.minValue || ''}
                  onChange={(e) => onFiltersChange({
                    minValue: e.target.value ? Number(e.target.value) : undefined
                  })}
                />
                <span className="text-muted-foreground">a</span>
                <Input
                  type="number"
                  placeholder="Máximo"
                  value={filters.maxValue || ''}
                  onChange={(e) => onFiltersChange({
                    maxValue: e.target.value ? Number(e.target.value) : undefined
                  })}
                />
              </div>
            </div>

            {/* Apply Button */}
            <div className="flex items-end">
              <Button
                className="w-full"
                disabled={loading}
                onClick={() => {
                  // Trigger refetch - this would be handled by parent component
                }}
              >
                <Clock className="h-4 w-4 mr-2" />
                {loading ? 'Aplicando...' : 'Aplicar Filtros'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}